import {fromEvent, iif, merge, of} from 'rxjs';
import {map, mergeMap, pairwise, pluck, startWith, tap} from 'rxjs/operators';
import {isNil, find, indexOf} from 'lodash';
import * as qs from 'app/lib/helperjs';
import {StringHelper} from 'app/lib/stringHelper';

export const CONTAINER_ATTRIBUTE = 'data-tab-container';
export const TAB_ATTRIBUTE = 'data-tab-target';
export const CONTENT_ATTRIBUTE = 'data-tab';
export const MOBILE_NAVIGATION_SELECTOR = 'data-tab-navigation';
export const ACTIVE_CLASS = 'active';

class TabsHandler {
    constructor() {
        this.container = qs.querySelector(`[${CONTAINER_ATTRIBUTE}]`);
        this.contents = qs.querySelectorAll(`[${CONTENT_ATTRIBUTE}]`);
        this.buttons = qs.querySelectorAll(`[${TAB_ATTRIBUTE}]`);
        this.mobileNavigation = qs.querySelectorAll(`[${MOBILE_NAVIGATION_SELECTOR}]`);
        this.index = 0;
    }

    ready() {
        if (!this.container || !this.contents || !this.buttons || !this.mobileNavigation) {
            return;
        }

        if (this.contents.length !== this.buttons.length) {
            throw new Error('Contents and tabs amounts are not equal');
        }

        this._resolveInitialElement();

        const buttons$ = fromEvent(this.buttons, 'click')
            .pipe(
                tap((event) => event.preventDefault()),
                map(({target}) => indexOf(this.buttons, target)),
            );

        const navigation$ = fromEvent(this.mobileNavigation, 'click')
            .pipe(
                pluck('target'),
                map((target) => target.tagName.toLowerCase() !== 'button' ? target.parentElement : target),
                map((button) => button.getAttribute('data-tab-navigation')),
                mergeMap((direction) => iif(() => direction === 'prev', of(this._prev()), of(this._next())))
            );

        merge(buttons$, navigation$)
            .pipe(
                startWith(this.index),
                pairwise()
            )
            .subscribe(([prevIndex, currIndex]) => {
                this.index = currIndex;
                if (!isNil(prevIndex)) {
                    qs.removeClass(this.buttons[prevIndex], ACTIVE_CLASS);
                    qs.removeClass(this.contents[prevIndex], ACTIVE_CLASS);

                }
                qs.addClass(this.buttons[currIndex], ACTIVE_CLASS);

                qs.addClass(this.contents[currIndex], ACTIVE_CLASS);
                this._setParentScrollPosition(this.buttons[currIndex]);
                this._toggleNavigationClickability();
                this._setLocationHash(currIndex);
            });
    }

    /** @private */
    _prev() {
        return Math.max(this.index - 1, 0);
    }

    /** @private */
    _next() {
        return Math.min(this.index + 1, this.buttons.length);
    }

    /** @private */
    _resolveInitialElement() {
        const hashLocation = StringHelper.decodeCyrillicFromUtf8(window.location.hash.replace(/^#/, ''));

        this.index = 0;

        this.buttons.forEach((button, index) => {
            if (hashLocation.includes(button.getAttribute(TAB_ATTRIBUTE))) {
                this.index = index;
            }
        });

        this._setParentScrollPosition(this.buttons[this.index]);

        qs.addClass(this.buttons[this.index], ACTIVE_CLASS);
        qs.addClass(this.contents[this.index], ACTIVE_CLASS);
    }

    /** @private */
    _setParentScrollPosition(element) {
        this.container.scrollLeft = element.offsetLeft;
        this.container.scrollTop = 0;
    }

    /** @private */
    _setLocationHash(currIndex) {
        if (isNil(currIndex)) {
            return;
        }

        const target = this.buttons[currIndex].getAttribute(TAB_ATTRIBUTE);

        history.pushState({}, '', '#' + target);
    }

    /** @private */
    _toggleNavigationClickability() {
        const prev = find(this.mobileNavigation, (element) => element.getAttribute(MOBILE_NAVIGATION_SELECTOR) === 'prev');
        const next = find(this.mobileNavigation, (element) => element.getAttribute(MOBILE_NAVIGATION_SELECTOR) === 'next');

        qs.addClass(this.mobileNavigation, ACTIVE_CLASS);

        if (this.index === 0) {
            qs.removeClass(prev, ACTIVE_CLASS);

        }
        if (this.index === this.buttons.length - 1) {
            qs.removeClass(next, ACTIVE_CLASS);
        }
    }
}

const tabsHandler = new TabsHandler();

export const Tab = () => tabsHandler.ready();
